import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-alpine.css";
import { SquarePen, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "react-toastify";
import ListView from "../../shared/ListView";
import ConfirmModal from "../../shared/ConfirmModal";
import { useGetCustomersQuery, useSaveCustomerMutation, useDeleteCustomerMutation, useBulkDeleteCustomersMutation } from "../../../../services/redux/api/components";

const CustomerMasterList = () => {
  const navigate = useNavigate();
  const gridRef = useRef(null);

  // State variables
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [isFilterApplied, setIsFilterApplied] = useState(false);

  const [saveCustomer, { isLoading: saving }] = useSaveCustomerMutation();
  const [deleteCustomer, { isLoading: deleting }] = useDeleteCustomerMutation();
  const [bulkDelete, { isLoading: bulkDeleting }] = useBulkDeleteCustomersMutation();

  const isProcessing = saving || deleting || bulkDeleting;

  const [deleteId, setDeleteId] = useState(null);
  const [deleteMode, setDeleteMode] = useState("single");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedNames, setSelectedNames] = useState([]);
  const selectedIdsRef = useRef([]);
  const [allSelected, setAllSelected] = useState(false);
  const [preserveSelection, setPreserveSelection] = useState(false);

  // Filter and Sort states
  const [filterColumn, setFilterColumn] = useState("firstName");
  const [filterValue, setFilterValue] = useState("");
  const [columnOptions, setColumnOptions] = useState([]);
  const [columnDefs, setColumnDefs] = useState([]);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);


  // RTK Query hooks
  // const { data: customers, isLoading, error } = useGetCustomersQuery({
  //   pageNumber: currentPage,
  //   pageSize: itemsPerPage,
  //   sortField,
  //   sortOrder: sortDirection,
  //   columnName: isFilterApplied ? filterColumn : null,
  //   value: isFilterApplied ? filterValue : null,
  // });

  const {
    data: customers,
    isLoading,
    error,
    refetch,
  } = useGetCustomersQuery({
    pageNumber: currentPage,
    pageSize: itemsPerPage,
    sortField,
    sortOrder: sortDirection,
    ...(isFilterApplied && {
      searchTerm: filterColumn,
      searchValue: filterValue,
    }),
  });

  // Process customer data (RTK Query handles fetching automatically)
  const rowData = useMemo(() => {
    if (!customers?.data) return [];
    return Array.isArray(customers.data)
      ? customers.data.map(item => ({
        ...item,
        id: item.CustomerID,
      }))
      : [];
  }, [customers, isFilterApplied]);

  const totalCount = customers?.totalCount || rowData.length;

  //  Error handling
  useEffect(() => {
    if (error) {
      toast.error(error.message || "Failed to fetch customer master data");
    }
  }, [error]);

  // Responsive check
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Dropdown options
  useEffect(() => {
    const options = [
      { value: "firstName", label: "Name" },
      { value: "PAN", label: "PAN" },
      { value: "CustomerType", label: "Customer Type" },
      { value: "Aadhar", label: "Aadhar" },
      { value: "ShortName", label: "Short Name" },
      { value: "Mobile", label: "Mobile" },
      { value: "GroupID", label: "Group Code" },
      { value: "Is_Group_Head", label: "Group Head" },
    ];
    setColumnOptions(options);
  }, []);

  // Sorting
  const handleSort = (field) => {
    let direction = "asc";
    if (sortField === field) {
      direction = sortDirection === "asc" ? "desc" : sortDirection === "desc" ? null : "asc";
    }
    setSortField(direction ? field : null);
    setSortDirection(direction);
    setCurrentPage(1);
  };

  const createHeaderComponent = (field, displayName) => {
    return function CustomHeader() {
      const isSorted = sortField === field;
      const isAsc = sortDirection === "asc";

      return (
        <div
          className="cursor-pointer hover:text-brandRed flex items-center"
          onClick={() => handleSort(field)}
        >
          {displayName}
          {isSorted && (
            <span className="ml-1">
              {isAsc ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </span>
          )}
        </div>
      );
    };
  };

  // Update columnDefs
  useEffect(() => {
    const createColumnDefs = () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        width: 50,
        suppressMenu: true,
      },
      {
        headerName: "Name",
        field: "FullName",
        flex: 1,
        headerComponent: createHeaderComponent("FullName", "Name"),
      },
      {
        headerName: "Short Name",
        field: "ShortName",
        flex: 1,
        headerComponent: createHeaderComponent("ShortName", "Short Name"),
      },
      {
        headerName: "PAN",
        field: "PAN",
        flex: 1,
        headerComponent: createHeaderComponent("PAN", "PAN"),
      },
      {
        headerName: "Aadhar",
        field: "Aadhar",
        flex: 1,
        headerComponent: createHeaderComponent("Aadhar", "Aadhar"),
      },
      {
        headerName: "Customer Type",
        field: "CustomerType",
        flex: 1,
        headerComponent: createHeaderComponent("CustomerType", "Customer Type"),
      },

      {
        headerName: "Mobile",
        field: "Mobile",
        flex: 1,
        headerComponent: createHeaderComponent("Mobile", "Mobile"),
      },
      {
        headerName: "Group Code",
        field: "GroupID",
        flex: 1,
        headerComponent: createHeaderComponent("GroupID", "Group Code"),
      },
      {
        headerName: "Group Head",
        field: "Is_Group_Head",
        flex: 1,
        headerComponent: createHeaderComponent("Is_Group_Head", "Group Head"),
        cellRenderer: (params) => (params.value ? <span className="badge badge-yes">Yes</span> : <span className="badge badge-no">No</span>),
      },
      {
        headerName: "Action",
        field: "action",
        cellRenderer: (params) => (
          <div className="flex space-x-2 mt-1">
            <button
              className="edit-button"
              onClick={() =>
                navigate(`/customer-master/edit/${params.data.CustomerID}`)
              }
              title="Edit"
              onMouseDown={(e) => e.preventDefault()}
            >
              <SquarePen color="#787474" size={18} className="mt-1" />
            </button>
            <button
              className="delete-button"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteId(params.data.CustomerID);
                setDeleteMode("single");
                onOpen();
              }}
              title="Delete"
              onMouseDown={(e) => e.preventDefault()}
            >
              <Trash2 color="#787474" size={18} className="mt-1" />
            </button>
          </div>
        ),
        sortable: false,
      },
    ];

    setColumnDefs(createColumnDefs());
  }, [sortField, sortDirection]);

  const handleFilter = () => {
    if (!filterValue.trim()) {
      toast.warning("Please enter filter value");
      return;
    }
    setIsFilterApplied(true);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilterValue("");
    setIsFilterApplied(false);
    setCurrentPage(1);
  };

  // Delete modal handlers
  // const onOpen = () => {
  //   if (!gridRef.current?.api) return;
  //   try {
  //     const selectedNodes = gridRef.current.api.getSelectedNodes() || [];
  //     selectedIdsRef.current = selectedNodes.map((node) => node.data.CustomerID);
  //     setSelectedNames(selectedNodes.map((node) => node.data.FullName));
  //     setPreserveSelection(true);
  //     setIsOpen(true);
  //   } catch (error) {
  //     console.error("Error in onOpen:", error);
  //   }
  // };

  // const onClose = () => {
  //   setIsOpen(false);
  //   setAllSelected(false);
  //   setPreserveSelection(false);
  // };

  const confirmDelete = async () => {
    try {
      if (deleteMode === "multi" || allSelected) {
        await bulkDelete(selectedIdsRef.current).unwrap();
        toast.success("Customers deleted successfully");
        refetch();
      } else {
        await deleteCustomer(deleteId).unwrap();
        toast.success("Customer deleted successfully");
      }
      onClose();
    } catch (error) {
      toast.error("Failed to delete customer(s)");
      console.error("Delete error:", error);
    }
  };

  const handleItemsPerPageChange = (e) => {
    const newPageSize = parseInt(e.target.value, 10);
    setItemsPerPage(newPageSize);
    setCurrentPage(1);
  };

  const onSelectionChanged = () => {
    if (isOpen || !gridRef.current?.api) return;

    const selectedNodes = gridRef.current.api.getSelectedNodes() || [];
    const totalDisplayedRows = gridRef.current.api.getDisplayedRowCount() || 0;

    selectedIdsRef.current = selectedNodes.map((node) => node.data.AgencyID);
    setSelectedNames(selectedNodes.map((node) => node.data.AgencyName));

    setAllSelected(selectedNodes.length === totalDisplayedRows && totalDisplayedRows > 0);
    setDeleteMode(selectedNodes.length > 1 || allSelected ? "multi" : "single");
  };

  const onOpen = () => {
    const selectedNodes = gridRef.current.api.getSelectedNodes() || [];
    selectedIdsRef.current = selectedNodes.map((node) => node.data.AgencyID);
    setSelectedNames(selectedNodes.map((node) => node.data.AgencyName));
    setPreserveSelection(true);
    setIsOpen(true);
  };

  const onClose = () => {
    setIsOpen(false);
    setAllSelected(false);
    setPreserveSelection(false);
  };

  useEffect(() => {
    const totalPages = Math.ceil((customers?.totalCount || 0) / itemsPerPage);
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [customers?.totalCount, currentPage, itemsPerPage]);



  return (
    <div className="container p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-brandRed">
          Customer Master List
        </h2>
        <button
          className="px-4 py-2 addListTable-button"
          onClick={() => navigate("/customer-master/add")}
        >
          Add
        </button>
      </div>

      <ListView
        filterColumn={filterColumn}
        setFilterColumn={setFilterColumn}
        columnOptions={columnOptions}
        filterValue={filterValue}
        setFilterValue={setFilterValue}
        handleFilter={handleFilter}
        clearFilters={clearFilters}
        isFilterApplied={isFilterApplied}
        gridRef={gridRef}
        columnDefs={columnDefs}
        rowData={rowData}
        paginatedData={rowData}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        setCurrentPage={setCurrentPage}
        onSelectionChanged={onSelectionChanged}
        defaultColDef={{ sortable: true, resizable: true, suppressMenu: true }}
        loading={isLoading || isProcessing}
        handleItemsPerPageChange={handleItemsPerPageChange}
        totalCount={customers?.totalCount || 0}
      />

      <ConfirmModal
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={confirmDelete}
        title="Confirm Deletion"
        message={
          allSelected
            ? `Are you sure you want to delete all ${rowData.length} customers on this page?`
            : deleteMode === "multi"
              ? `Are you sure you want to delete ${selectedIdsRef.current.length} selected customers?`
              : "Are you sure you want to delete this customer?"
        }
        selectedNames={selectedNames}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmColor="backButton"
      />
    </div>
  );
};

export default CustomerMasterList;
